import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import { useAuth } from './AuthContext'
import { getToken } from '../services/auth'
import { socketService } from '../services/socket'

interface SocketContextValue {
  on: (event: string, handler: (...args: any[]) => void) => void
  off: (event: string, handler: (...args: any[]) => void) => void
}

const SocketContext = createContext<SocketContextValue>({
  on: () => {},
  off: () => {},
})

/**
 * Provides Socket.IO connection tied to auth state
 */
export function SocketProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { isAuthenticated } = useAuth()
  const connectedRef = useRef(false)

  useEffect(() => {
    if (!isAuthenticated) {
      if (connectedRef.current) {
        socketService.disconnect()
        connectedRef.current = false
      }
      return
    }

    const token = getToken()
    if (!token) return

    socketService.connect(token)
    connectedRef.current = true

    return () => {
      socketService.disconnect()
      connectedRef.current = false
    }
  }, [isAuthenticated])

  const on = useCallback((event: string, handler: (...args: any[]) => void) => {
    socketService.on(event, handler)
  }, [])

  const off = useCallback((event: string, handler: (...args: any[]) => void) => {
    socketService.off(event, handler)
  }, [])

  return (
    <SocketContext.Provider value={{ on, off }}>
      {children}
    </SocketContext.Provider>
  )
}

export function useSocket(): SocketContextValue {
  return useContext(SocketContext)
}

export default SocketContext
